/**
 * Cart-first draft order checkout for customized dresses (_custom_price line items).
 */
(function () {
  const CONFIG = window.BridalDraftCheckout || {};
  const URL_PARAMS = window.BridalUrlParams;
  const REQUEST_TIMEOUT_MS = 30000;
  const DEFAULT_API_BASE = 'https://app.thebebride.com/api';

  const INTERNAL_LINE_KEYS = new Set([
    'custom',
    '_custom',
    '_custom_price',
    'product_parts_ids',
    '_product_parts_ids',
    'product_meta',
    '_product_meta',
    '_snapshot_url',
    'snapshot_url',
    'accessory_variant_ids',
    '_accessory_variant_ids',
    'customization_snapshot',
    'custom_dress_name',
    '_custom_dress_name',
  ]);

  /** @returns {boolean} */
  function isDraftCheckoutEnabled() {
    return CONFIG.enabled !== false && CONFIG.enabled !== 'false';
  }

  /** @returns {string} */
  function getApiBase() {
    const normalized = normalizeApiBase(CONFIG.apiBase);
    return normalized || DEFAULT_API_BASE;
  }

  /** @param {string} base */
  function normalizeApiBase(base) {
    let u = String(base || '').trim();
    if (!u) return '';
    while (u.endsWith('/')) u = u.slice(0, -1);
    return u;
  }

  /** Legacy visible keys → hidden underscore-prefixed cart properties. */
  const LEGACY_VISIBLE_PROPERTY_MIGRATIONS = [
    ['product_meta', '_product_meta'],
    ['product_parts_ids', '_product_parts_ids'],
    ['snapshot_url', '_snapshot_url'],
    ['accessory_variant_ids', '_accessory_variant_ids'],
    ['custom_dress_name', '_custom_dress_name'],
  ];

  /**
   * @param {Record<string, string>} props
   * @returns {Record<string, string> | null}
   */
  function migrateLegacyVisibleCartProperties(props) {
    if (!props || typeof props !== 'object') return null;
    let changed = false;
    const next = { ...props };

    for (const [visibleKey, hiddenKey] of LEGACY_VISIBLE_PROPERTY_MIGRATIONS) {
      const raw = next[visibleKey];
      if (raw == null || String(raw).trim() === '') continue;
      if (!next[hiddenKey]) next[hiddenKey] = String(raw);
      delete next[visibleKey];
      changed = true;
    }

    return changed ? next : null;
  }

  /**
   * Hide legacy internal line properties before Shopify checkout.
   * @param {{ items?: unknown[] }} cart
   */
  async function sanitizeCartInternalProperties(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];

    for (let index = 0; index < items.length; index++) {
      const props = getLineProperties(items[index]);
      const migrated = migrateLegacyVisibleCartProperties(props);
      if (!migrated) continue;

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ line: index + 1, properties: migrated }),
      });
    }
  }

  async function goToShopifyCheckout(cart) {
    if (cart) {
      try {
        await sanitizeCartInternalProperties(cart);
      } catch (err) {
        console.warn('[bridal-draft-checkout] cart property migration failed:', err);
      }
    }
    window.location.assign('/checkout');
  }

  /**
   * @param {unknown} item
   * @returns {Record<string, string>}
   */
  function getLineProperties(item) {
    const p = item && typeof item === 'object' && 'properties' in item ? item.properties : null;
    if (!p) return {};
    if (Array.isArray(p)) {
      const out = /** @type {Record<string, string>} */ ({});
      for (const entry of p) {
        if (entry && typeof entry === 'object' && 'name' in entry) {
          out[String(entry.name)] = entry.value != null ? String(entry.value) : '';
        }
      }
      return out;
    }
    if (typeof p === 'object') {
      return /** @type {Record<string, string>} */ (p);
    }
    return {};
  }

  /**
   * @param {Record<string, string>} props
   * @returns {boolean}
   */
  function lineHasCustomPrice(props) {
    const raw = props._custom_price ?? props['_custom_price'];
    return raw != null && String(raw).trim() !== '';
  }

  /** @param {string} email */
  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * @param {HTMLElement} root
   * @param {string} message
   */
  function showError(root, message) {
    const el = root.querySelector('#bridal-draft-checkout-error');
    if (!(el instanceof HTMLElement)) return;
    el.textContent = message;
    el.hidden = false;
    el.setAttribute('aria-live', 'polite');
  }

  /** @param {HTMLElement} root */
  function clearError(root) {
    const el = root.querySelector('#bridal-draft-checkout-error');
    if (!(el instanceof HTMLElement)) return;
    el.textContent = '';
    el.hidden = true;
  }

  /**
   * @param {Record<string, string>} lineProps
   * @returns {Record<string, string>}
   */
  function linePropertiesToObject(lineProps) {
    const out = /** @type {Record<string, string>} */ ({});
    if (!lineProps || typeof lineProps !== 'object') return out;
    for (const [key, value] of Object.entries(lineProps)) {
      if (INTERNAL_LINE_KEYS.has(key)) continue;
      if (value == null || String(value).trim() === '') continue;
      out[key] = String(value);
    }
    return out;
  }

  /**
   * @param {string} raw
   * @returns {Record<string, string>}
   */
  function parseProductMetaFromLine(raw) {
    if (!URL_PARAMS?.parseProductMeta) return {};
    return URL_PARAMS.parseProductMeta(raw);
  }

  /**
   * @param {string} raw
   * @returns {Record<string, string>}
   */
  function parsePartsIdsProperties(raw) {
    if (!raw || !URL_PARAMS?.parseProperties) return {};
    return URL_PARAMS.parseProperties(raw);
  }

  /**
   * Draft checkout runs when any cart line has _custom_price.
   * @param {{ items?: unknown[] }} cart
   * @returns {boolean}
   */
  function cartHasCustomDressLine(cart) {
    if (!cart?.items?.length) return false;
    return cart.items.some((item) => lineHasCustomPrice(getLineProperties(item)));
  }

  /**
   * @param {Record<string, string>} props
   * @returns {number}
   */
  function customPriceCentsFromProps(props) {
    return Number(String(props._custom_price || '').replace(/\D/g, '')) || 0;
  }

  /**
   * Configurator accessory variant IDs (not every cart line).
   * @param {Record<string, string>} props
   * @returns {string[]}
   */
  function accessoryVariantIdsFromProps(props) {
    const raw = props._accessory_variant_ids ?? props.accessory_variant_ids;
    if (!raw) return [];
    return String(raw)
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  /**
   * @param {unknown} item
   * @returns {string}
   */
  function productHandleFromCartItem(item) {
    if (item && typeof item === 'object' && 'handle' in item && item.handle) {
      return String(item.handle);
    }
    const url =
      item && typeof item === 'object' && 'url' in item ? String(item.url) : '';
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  /**
   * @param {{ items?: unknown[] }} cart
   * @returns {string[]}
   */
  function collectConfiguratorAccessoryVariantIds(cart) {
    const ids = new Set();
    for (const item of cart.items || []) {
      const props = getLineProperties(item);
      if (!lineHasCustomPrice(props)) continue;
      for (const id of accessoryVariantIdsFromProps(props)) {
        ids.add(id);
      }
    }
    return [...ids];
  }

  /**
   * @param {unknown} item
   * @returns {object}
   */
  function buildCartLineItem(item) {
    const props = getLineProperties(item);
    const isCustom = lineHasCustomPrice(props);
    const productId = String(
      item && typeof item === 'object' && 'product_id' in item ? item.product_id : ''
    );
    const qty = Math.max(1, Number(item && typeof item === 'object' && 'quantity' in item ? item.quantity : 1) || 1);

    let productMeta = isCustom ? parseProductMetaFromLine(props._product_meta || props.product_meta || '') : {};
    let snapshotUrl = isCustom && (props._snapshot_url || props.snapshot_url) ? String(props._snapshot_url || props.snapshot_url) : '';
    let properties = linePropertiesToObject(props);

    if (isCustom) {
      const partsParsed = parsePartsIdsProperties(
        props._product_parts_ids || props.product_parts_ids || ''
      );
      properties = { ...partsParsed, ...properties };

      if (URL_PARAMS?.readConfiguratorFromStorage && productId) {
        const stored = URL_PARAMS.readConfiguratorFromStorage(productId);
        if (stored) {
          if (!Object.keys(productMeta).length && stored.productMeta) {
            productMeta = stored.productMeta;
          }
          if (!snapshotUrl && stored.snapshotUrl) snapshotUrl = stored.snapshotUrl;
          if (stored.properties) {
            for (const [key, value] of Object.entries(stored.properties)) {
              if (INTERNAL_LINE_KEYS.has(key)) continue;
              if (value == null || String(value).trim() === '') continue;
              properties[key] = String(value);
            }
          }
        }
      }

      properties = linePropertiesToObject(properties);
    }

    let unitPriceCents = 0;
    if (isCustom) {
      unitPriceCents = customPriceCentsFromProps(props);
    } else if (item && typeof item === 'object') {
      unitPriceCents = Number(
        /** @type {{ final_price?: number; price?: number }} */ (item).final_price ??
          /** @type {{ price?: number }} */ (item).price ??
          0
      );
    }

    const variantId = Number(
      item && typeof item === 'object' && 'variant_id' in item ? item.variant_id : 0
    );

    const customPriceRaw =
      isCustom && props._custom_price != null ? String(props._custom_price).trim() : '';

    return {
      variantId,
      quantity: qty,
      productHandle: productHandleFromCartItem(item),
      productId: productId ? Number(productId) : null,
      title: String(
        item && typeof item === 'object'
          ? /** @type {{ product_title?: string; title?: string }} */ (item).product_title ||
              /** @type {{ title?: string }} */ (item).title ||
              ''
          : ''
      ),
      isCustom,
      /** Raw cart property (cents), same as cart.js line item properties._custom_price */
      _custom_price: customPriceRaw || null,
      price: unitPriceCents > 0 ? unitPriceCents / 100 : null,
      priceCents: unitPriceCents > 0 ? unitPriceCents : null,
      productMeta,
      properties,
      snapshotUrl: snapshotUrl || null,
      customDressName: props['Dress name'] ? String(props['Dress name']).trim() : null,
    };
  }

  /**
   * All cart lines; custom lines use _custom_price, others use Shopify line price.
   * @param {{ items?: unknown[] }} cart
   * @param {string} email
   * @returns {object}
   */
  function buildDraftPayload(cart, email) {
    const lineItems = (cart.items || []).map((item) => buildCartLineItem(item));

    return {
      email,
      sendInvoice: true,
      lineItems,
      /** Configurator accessory variant IDs not added as separate cart rows. */
      accessoryVariants: collectConfiguratorAccessoryVariantIds(cart),
    };
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} loading
   */
  function setLoading(button, loading) {
    if (loading) {
      button.dataset.bridalCheckoutOriginalText = button.textContent || '';
      button.textContent = 'Preparing your order…';
      button.disabled = true;
    } else {
      const orig = button.dataset.bridalCheckoutOriginalText;
      if (orig) button.textContent = orig;
      button.disabled = false;
    }
  }

  /**
   * @param {string} apiBase
   * @param {object} body
   */
  async function postDraftOrder(apiBase, body) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    /** @type {Record<string, string>} */
    const headers = { 'Content-Type': 'application/json' };
    if (CONFIG.themeKey) headers['X-Bridal-Theme-Key'] = CONFIG.themeKey;

    try {
      const res = await fetch(`${apiBase}/draft-orders`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
        credentials: 'omit',
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg =
          (data && (data.message || data.error)) ||
          `Unable to create your order (${res.status}). Please try again.`;
        throw new Error(msg);
      }

      const redirectUrl = data.invoiceUrl || data.checkoutUrl;
      if (!redirectUrl) throw new Error('Order created but no payment link was returned. Please contact support.');

      window.location.href = redirectUrl;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * @param {HTMLButtonElement | null} button
   */
  async function handleCheckout(button) {
    if (!isDraftCheckoutEnabled()) {
      let cart;
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        cart = await res.json();
      } catch {
        cart = null;
      }
      await goToShopifyCheckout(cart);
      return;
    }

    const apiBase = getApiBase();
    const section =
      button.closest('.cart__checkout-section, cart-drawer-component, .cart-drawer, main') ||
      document;

    let cart;
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      cart = await res.json();
    } catch {
      await goToShopifyCheckout();
      return;
    }

    try {
      await sanitizeCartInternalProperties(cart);
      const refreshed = await fetch('/cart.js', { credentials: 'same-origin' });
      cart = await refreshed.json();
    } catch (err) {
      console.warn('[bridal-draft-checkout] cart property migration failed:', err);
    }

    if (!cartHasCustomDressLine(cart)) {
      await goToShopifyCheckout(cart);
      return;
    }

    if (!cart.items?.length) {
      await goToShopifyCheckout(cart);
      return;
    }

    clearError(section);

    const emailInput = section.querySelector('.cart__checkout-email');
    let email =
      emailInput instanceof HTMLInputElement ? emailInput.value.trim() : '';
    if (!email && CONFIG.customerEmail) email = String(CONFIG.customerEmail).trim();

    if (!email || !isValidEmail(email)) {
      showError(
        section,
        'Please enter a valid email address to complete your order.'
      );
      if (emailInput instanceof HTMLInputElement) emailInput.focus();
      return;
    }

    if (button) setLoading(button, true);

    try {
      const payload = buildDraftPayload(cart, email);
      await postDraftOrder(apiBase, payload);
    } catch (err) {
      const message =
        err instanceof Error && err.name === 'AbortError'
          ? 'The request timed out. Please check your connection and try again.'
          : err instanceof Error
            ? err.message
            : 'Something went wrong. Please try again.';
      showError(section, message);
      if (button) setLoading(button, false);
    }
  }

  /**
   * @param {Event} event
   */
  function onCheckoutClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest(
      '#checkout, [name="checkout"], [data-bridal-checkout-trigger]'
    );
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    void handleCheckout(button);
  }

  /**
   * Fallback: native cart form submit with name=checkout (e.g. cart page).
   * @param {Event} event
   */
  function onCartFormSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

    const submitter = event.submitter;
    const isCheckout =
      (submitter instanceof HTMLButtonElement && submitter.name === 'checkout') ||
      (submitter instanceof HTMLInputElement && submitter.name === 'checkout');

    if (!isCheckout) return;

    event.preventDefault();
    event.stopPropagation();

    const button =
      submitter instanceof HTMLButtonElement
        ? submitter
        : document.querySelector('#checkout[data-bridal-checkout-trigger], #checkout[name="checkout"]');

    void handleCheckout(button instanceof HTMLButtonElement ? button : null);
  }

  function initSummary() {
    const summary = document.getElementById('bridal-configurator-summary');
    if (!(summary instanceof HTMLElement)) return;

    const productId = summary.dataset.productId || '';
    if (!URL_PARAMS?.resolveConfigurator || !productId) return;

    const config = URL_PARAMS.resolveConfigurator(productId);
    if (!config) return;

    let visible = false;

    const mediaWrap = summary.querySelector('.bridal-configurator-summary__media');
    const img = summary.querySelector('.bridal-configurator-summary__image');
    if (config.snapshotUrl && img instanceof HTMLImageElement) {
      img.src = config.snapshotUrl;
      img.alt = config.customDressName || 'Your custom design';
      if (mediaWrap instanceof HTMLElement) mediaWrap.hidden = false;
      visible = true;
    }

    const nameEl = summary.querySelector('.bridal-configurator-summary__name');
    const dressInput = document.querySelector('[data-dress-name-line-property]');
    const dressName =
      config.customDressName ||
      (dressInput instanceof HTMLInputElement ? dressInput.value.trim() : '');
    if (dressName && nameEl instanceof HTMLElement) {
      nameEl.textContent = dressName;
      nameEl.hidden = false;
      visible = true;
    }

    const priceEl = summary.querySelector('.bridal-configurator-summary__price');
    if (config.price && priceEl instanceof HTMLElement) {
      const formatted =
        typeof window.__beCustomProductPriceFormat === 'function'
          ? window.__beCustomProductPriceFormat(config.price)
          : config.price;
      if (formatted) {
        priceEl.textContent = formatted;
        priceEl.hidden = false;
        visible = true;
      }
    }

    if (visible) summary.hidden = false;
  }

  function init() {
    document.addEventListener('click', onCheckoutClick, true);
    document.addEventListener('submit', onCartFormSubmit, true);
    initSummary();

    document.querySelectorAll('.cart__checkout-email').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (input.value.trim() || !CONFIG.customerEmail) return;
      input.value = String(CONFIG.customerEmail);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
